/**
 * Boot orchestrator (Phase 1).
 *
 * Wires VisualizerEngine to the DOM:
 *   - registers the Geiss preset pack,
 *   - opens the onboarding modal to pick an audio source,
 *   - drives the rAF loop,
 *   - routes keyboard hotkeys (presets, fullscreen, HUD, beat, scale),
 *   - feeds the HUD with engine readouts.
 *
 * `main.ts` only handles top-level error surfacing; everything wiring-related
 * lives here so it stays unit-friendly later.
 */

import { Hud } from './hud';
import { KeyboardRouter } from './keyboard';
import { openOnboarding, type AudioChoice } from './onboarding';
import { showError } from './errorScreen';
import { VisualizerEngine } from '../engine/core/VisualizerEngine';
import { AUTO_CYCLER_CHILDREN, geissPresets } from '../engine/presets/geiss/index';
import { startDemoClip } from '../audio/capture/demoClip';
import { startMicrophone } from '../audio/capture/microphone';
import {
  DisplayAudioMissingError,
  DisplayMediaUnavailableError,
  startDisplayMediaAudio,
} from '../audio/capture/displayMedia';
import { nextAutoBeat } from '../audio/analysis/beatTest';
import { PHASE0_INTERNAL_SCALE } from '../engine/phase0Reference';
import { PresentMode } from '../renderer/webgpu/passes/presentPass';

const PRESENT_MODE_LABELS: Record<PresentMode, string> = {
  [PresentMode.Normal]: 'normal',
  [PresentMode.GrayscaleRamp]: 'test: ramp',
  [PresentMode.ColorBars]: 'test: bars',
  [PresentMode.HdrPatch]: 'test: hdr',
};

type AudioSourceHandle = {
  output: AudioNode;
  stop: () => void;
  label: string;
};

export async function boot(canvas: HTMLCanvasElement): Promise<void> {
  const engine = await VisualizerEngine.create(canvas, {
    internalRenderScale: PHASE0_INTERNAL_SCALE,
  });
  engine.registerPresets(geissPresets);
  engine.configureAutoCycler(AUTO_CYCLER_CHILDREN);
  // Default boot preset is the auto-DJ — Geiss-style cycling between effects
  // driven by time + beat + energy. Press '2' (phase 0 reference) or any
  // other slot to take manual control; press '1' to re-enter auto.
  engine.loadPreset('auto');

  let activeSource: AudioSourceHandle | null = null;
  let audioLabel = '(none yet — pick a source)';

  async function activateChoice(choice: AudioChoice): Promise<void> {
    // Audio APIs only run after a user gesture; the modal click counts.
    await engine.resumeAudio();
    let next: AudioSourceHandle;
    switch (choice) {
      case 'tab':
        next = await startTab(engine.audio.ctx);
        break;
      case 'mic':
        next = await startMic(engine.audio.ctx);
        break;
      case 'demo':
        next = await startDemo(engine.audio.ctx);
        break;
    }
    if (activeSource) {
      try {
        engine.detachAudioSource(activeSource.output);
        activeSource.stop();
      } catch (err) {
        console.warn('[audio] failed to stop previous source', err);
      }
    }
    engine.attachAudioSource(next.output);
    activeSource = next;
    audioLabel = next.label;
  }

  // Open onboarding immediately. Engine is already rendering silent feedback.
  openOnboarding({ onChoose: activateChoice });

  // ---- Keyboard ----------------------------------------------------------

  const keys = new KeyboardRouter();
  const hud = new Hud();

  keys.on('h', () => hud.toggle());
  keys.on('b', () => engine.beatInjector.pulse());
  keys.on('a', () => {
    engine.beatInjector.setIntervalMs(nextAutoBeat(engine.beatInjector.getIntervalMs()));
  });
  keys.on('t', () => engine.cyclePresentMode());
  keys.on('o', () => openOnboarding({ onChoose: activateChoice }));
  keys.on('f', () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      canvas.requestFullscreen().catch(() => {});
    }
  });
  keys.on('[', () => engine.bumpInternalScale(-0.05));
  keys.on(']', () => engine.bumpInternalScale(0.05));
  keys.on('arrowright', () => engine.nextPreset());
  keys.on('arrowleft', () => engine.prevPreset());
  keys.on(' ', () => engine.randomPreset());
  for (let i = 1; i <= 9; i++) {
    const idx = i - 1;
    keys.on(String(i), () => engine.loadPresetAt(idx));
  }
  keys.on('0', () => engine.loadPresetAt(9));

  keys.attach(window);

  // Surface a permanent message if the GPU device is lost mid-flight.
  engine.device.lost
    .then((info) => {
      showError(
        'GPU device lost',
        `Reason: ${info.reason}. ${info.message ?? ''}`.trim(),
      );
    })
    .catch(() => {});

  // ---- Frame loop --------------------------------------------------------

  function frame(nowMs: number): void {
    const info = engine.step(nowMs);
    hud.update(nowMs, {
      fps: info.fps,
      frameMs: info.frameMs,
      preset: info.presetName,
      internalScale: info.internalScale,
      internalSize: info.internalSize,
      rms: info.audio.rms,
      bass: info.audio.bass,
      mid: info.audio.mid,
      treble: info.audio.treble,
      beat: {
        tick: info.audio.beat.tick,
        source: info.beatSource,
        confidence: info.audio.beat.confidence,
        flux: info.audio.beat.flux,
        threshold: info.audio.beat.threshold,
        bpm: info.audio.beat.bpm,
        msSinceLastBeat: info.audio.beat.msSinceLastBeat,
      },
      audioSource: audioLabel,
      presentMode: PRESENT_MODE_LABELS[info.presentMode],
      autoBeatMs: info.beatInjectorMs,
      auto: info.auto,
      transitionT: info.transitionT,
      warpField: info.warpField,
    });
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}

// ---- Audio source factories (each one funnels into AudioSourceHandle) ------

async function startDemo(ctx: AudioContext): Promise<AudioSourceHandle> {
  const src = await startDemoClip(ctx);
  return { output: src.output, stop: src.stop, label: src.label };
}

async function startMic(ctx: AudioContext): Promise<AudioSourceHandle> {
  try {
    const src = await startMicrophone(ctx);
    return { output: src.output, stop: src.stop, label: src.label };
  } catch (err) {
    throw new Error(
      `Microphone unavailable: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

async function startTab(ctx: AudioContext): Promise<AudioSourceHandle> {
  try {
    const src = await startDisplayMediaAudio(ctx);
    return { output: src.output, stop: src.stop, label: src.label };
  } catch (err) {
    if (err instanceof DisplayAudioMissingError) throw err;
    if (err instanceof DisplayMediaUnavailableError) throw err;
    throw new Error(
      `Tab capture failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}
